import { tx } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';
import { surfaceBackground, type SurfaceToken } from '../_shared/surface';
import { RICH_TEXT_CSS } from '../_shared/RichHtmlContent';

/**
 * Renders operator-authored content into a chosen surface. Pairs with
 * the admin's RichTextEditor (TinyMCE, emitting HTML) for the
 * default {@code 'html'} mode; absorbs the former {@code HtmlBlock}
 * raw-paste primitive via the {@code 'raw-html'} mode.
 *
 * <h3>Format modes</h3>
 * <ul>
 *   <li>{@code format='html'} (default) — themed surface: render via
 *       {@code dangerouslySetInnerHTML} inside a {@link SectionShell}
 *       with a max-width clamp and the {@code lms-rich-text} class so
 *       themes can scope global typography rules (headings, lists,
 *       blockquotes, code, links, images).</li>
 *   <li>{@code format='markdown'} — render as preformatted text. We
 *       deliberately don't ship a Markdown renderer client-side — it'd
 *       add a 50KB+ dep for content that's already migrating to HTML.
 *       Existing Markdown content keeps rendering as preformatted text
 *       so authors notice and re-edit through the proper editor.</li>
 *   <li>{@code format='raw-html'} — raw-paste escape hatch: bare
 *       {@code <div dangerouslySetInnerHTML>} with NO SectionShell,
 *       NO width clamp, NO {@code lms-rich-text} class. Use for
 *       handcrafted markup that must NOT pick up themed typography
 *       rules. Trusted-operator contexts only — rendered verbatim.</li>
 * </ul>
 *
 * <p>Editor-side sanitisation (basic block-element whitelist +
 * attribute strip) is the trust boundary; this component renders the
 * stored content as-is.
 *
 * <p>Direct port of ThemeMaker's {@code blocks/text/RichText/RichText.tsx},
 * with the {@code 'raw-html'} mode absorbing the former HtmlBlock.
 */
const widthMap: Record<string, number> = {
  narrow: 640,
  default: 760,
  wide: 960,
};

export interface RichTextProps {
  format?: 'html' | 'markdown' | 'raw-html';
  body?: unknown;
  maxWidth?: keyof typeof widthMap;
  /**
   * Themed background the content sits on. Defaults to {@code 'none'}
   * (transparent, full-bleed) so existing sections are unchanged; set
   * to {@code 'card'} for a white (theme-surface) panel that fills the
   * containing section — used by the CMS HTML-page body. Ignored in
   * {@code 'raw-html'} mode (no SectionShell there).
   */
  surface?: SurfaceToken;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

export function RichText({
  format = 'html',
  body,
  maxWidth = 'default',
  surface = 'none',
  paddingOuter,
  paddingInner,
}: RichTextProps) {
  const text = tx(body);
  const width = widthMap[maxWidth] ?? widthMap['default']!;

  // Raw-paste primitive (absorbed HtmlBlock): no SectionShell, no
  // width clamp, no themed-typography class. Renders verbatim.
  if (format === 'raw-html') {
    return <div dangerouslySetInnerHTML={{ __html: text }} />;
  }

  return (
    <SectionShell
      background={surfaceBackground(surface)}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <div
        // Single class so themes can scope global typography rules to
        // editor content without affecting block-internal text.
        className="lms-rich-text"
        style={{
          maxWidth: 'calc(var(--app-max-width, 100%) * 1px)',
          margin: '0 auto',
          fontSize: 'var(--font-size-md, 16px)',
          lineHeight: 1.7,
          color: 'var(--color-text-primary, #0f1218)',
        }}
      >
        {format === 'html' ? (
          <div dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'var(--font-family-body, inherit)',
              margin: 0,
            }}
          >
            {text}
          </pre>
        )}
      </div>
      <style dangerouslySetInnerHTML={{ __html: RICH_TEXT_CSS }} />
    </SectionShell>
  );
}
